// AuthContext — Emergent-managed Google auth.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Auth, SESSION_KEY } from "./api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem(SESSION_KEY);
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const me = await Auth.me();
      setUser(me);
    } catch {
      localStorage.removeItem(SESSION_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // On boot: check URL hash for session_id (from Emergent redirect), exchange via OUR
  // backend (server-to-server, avoids the demo backend's CORS allow-list), persist.
  useEffect(() => {
    const run = async () => {
      const hash = window.location.hash || "";
      const match = hash.match(/session_id=([^&]+)/);
      if (match) {
        const sid = decodeURIComponent(match[1]);
        let ok = false;
        try {
          // Auth.exchange POSTs to /api/auth/session and returns
          // { user_id, email, name, picture, session_token }
          const res = await Auth.exchange(sid);
          if (res?.session_token) {
            localStorage.setItem(SESSION_KEY, res.session_token);
            setUser({
              user_id: res.user_id,
              email: res.email,
              name: res.name,
              picture: res.picture,
            });
            ok = true;
          }
        } catch (e) {
          console.error("auth exchange failed", e);
          try {
            const { toast } = await import("sonner");
            const status = e?.response?.status;
            if (status === 401) {
              toast.error("Sign-in link expired. Please try again.");
            } else {
              toast.error("Sign-in didn't complete — please try again.");
            }
          } catch (_) {
            /* toast unavailable; swallow */
          }
        } finally {
          // ALWAYS clean the hash so a stale/expired session_id can't keep the
          // user stuck on the logged-out hero.
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
        }
        if (ok) {
          setLoading(false);
          return;
        }
        // Fall through to refresh() so we don't leave loading=true forever
      }
      await refresh();
    };
    run();
  }, [refresh]);

  const signIn = () => {
    // Always return to current origin root so AuthProvider picks up session_id
    const redirect = window.location.origin + "/";
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const signOut = async () => {
    try { await Auth.logout(); } catch { /* network error — ignore */ }
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, signIn, signOut, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
